import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { UserStatus } from "@dataconnect/generated";
import { useGetUserCredits } from "@dataconnect/generated/react";
import { useTranslation } from "react-i18next";
import { CreditCardIcon, CreditIcon } from "@/src/components/icons";
import { auth, functions } from "@/src/config/firebase";
import { httpsCallable } from "firebase/functions";
import { Skeleton } from "@/src/components/Skeleton";
import { ReferralHubCard } from "@/src/components/ReferralHub";
import { CreditGrantsTable } from "@/src/components/CreditGrantsTable";
import {
  COST_BG_REMOVE,
  COST_EDITS,
  COST_FAST_1K,
  COST_FAST_2K,
  COST_FAST_4K,
  COST_IMAGE_1K,
  COST_IMAGE_2K,
  COST_IMAGE_4K,
  COST_UPSCALE,
  getReachableUpscaleTiers,
  formatCreditAmount,
  getVideoGenerationCreditCost,
} from "@/src/utils/constants";
import { trackSubscriptionPageViewed } from "@/src/services/analyticsService";
import { MAX_OUTPUT_MEGAPIXELS } from "@/src/services/imageGenerationEngine";

interface AccountSettingsProps {
  hideHeader?: boolean;
}

/** Topaz tiers a user can actually hit under the current output cap. */
const reachableUpscaleTiers = getReachableUpscaleTiers(MAX_OUTPUT_MEGAPIXELS);

const VIDEO_PRICING_MODELS = [
  { id: "veo-3.1-fast-generate-001", labelKey: "video.model.fast" },
  { id: "veo-3.1-generate-001", labelKey: "video.model.quality" },
] as const;
const VIDEO_PRICING_RESOLUTIONS = ["720p", "1080p"] as const;
const VIDEO_PRICING_DURATIONS = [4, 6, 8] as const;

interface SubscriptionDetails {
  hasSubscription: boolean;
  subscriptionId?: string;
  status?: string;
  currentPeriodStart?: number;
  currentPeriodEnd?: number;
  planName?: string;
  amount?: number;
  currency?: string;
  interval?: string;
  usageData?: {
    aggregatedValue: number;
    startTime: number;
    endTime: number;
  };
}

function CreditAmountCell({
  value,
  suffix,
}: {
  value: number;
  /** Appended to the amount, e.g. "–4" to render a tiered range. */
  suffix?: string;
}) {
  return (
    <td className="py-3 px-4 text-right">
      <div className="inline-flex items-center justify-end gap-1.5 font-mono font-semibold text-gray-900">
        <span>
          {formatCreditAmount(value)}
          {suffix}
        </span>
        <CreditIcon className="w-4 h-4 text-gray-500" />
      </div>
    </td>
  );
}

interface UpcomingInvoice {
  id: string;
  amountDue: number;
  amountPaid: number;
  amountRemaining: number;
  currency: string;
  subtotal: number;
  total: number;
  tax: number | null;
  periodStart: number;
  periodEnd: number;
  nextPaymentAttempt: number | null;
  lineItems: Array<{
    description: string | null;
    amount: number;
    currency: string;
    quantity: number | null;
    period: {
      start: number;
      end: number;
    };
    proration: boolean;
    type: string;
  }>;
  totalDiscountAmounts: Array<{
    amount: number;
    discount: string;
  }>;
  totalTaxAmounts: Array<{
    amount: number;
    rate: string;
  }>;
}

export const AccountSettings: React.FC<AccountSettingsProps> = ({
  hideHeader = false,
}) => {
  const { t } = useTranslation();
  const [customerSessionClientSecret, setCustomerSessionClientSecret] =
    React.useState<string | null>(null);

  React.useEffect(() => {
    trackSubscriptionPageViewed();
  }, []);

  const { data, isLoading } = useGetUserCredits({
    enabled: !!auth.currentUser,
  });
  const userData = data?.user;

  // Fetch subscription details for ACTIVE users
  const { data: subscriptionData, isLoading: isLoadingSubscription } = useQuery(
    {
      queryKey: ["GetSubscriptionDetails", auth.currentUser?.uid],
      queryFn: async () => {
        const getSubscriptionDetails = httpsCallable<
          Record<string, never>,
          SubscriptionDetails
        >(functions, "getSubscriptionDetails", { timeout: 150_000 });
        const { data } = await getSubscriptionDetails({});
        return data;
      },
      enabled: userData?.status === UserStatus.ACTIVE,
    }
  );

  // Fetch upcoming invoice preview for ACTIVE users
  const { data: upcomingInvoice, isLoading: isLoadingInvoice } = useQuery({
    queryKey: ["GetUpcomingInvoice", auth.currentUser?.uid],
    queryFn: async () => {
      const getUpcomingInvoice = httpsCallable<
        Record<string, never>,
        UpcomingInvoice
      >(functions, "getUpcomingInvoice", { timeout: 150_000 });
      const { data } = await getUpcomingInvoice({});
      return data;
    },
    enabled:
      userData?.status === UserStatus.ACTIVE &&
      subscriptionData?.hasSubscription === true,
  });

  // Calculate total usage in current billing period from Stripe data
  const totalUsage = React.useMemo(() => {
    // Use Stripe usage data
    if (subscriptionData?.usageData?.aggregatedValue) {
      return subscriptionData.usageData.aggregatedValue;
    }
    return 0;
  }, [subscriptionData?.usageData]);

  React.useEffect(() => {
    if (userData?.stripeCustomerId) {
      const fetchClientSecret = async () => {
        try {
          const createStripeCustomerSession = httpsCallable(
            functions,
            "createStripeCustomerSession",
            { timeout: 150_000 }
          );
          const { data } = await createStripeCustomerSession({});
          // @ts-ignore
          if (data.clientSecret) {
            // @ts-ignore
            setCustomerSessionClientSecret(data.clientSecret);
          }
        } catch (error) {
          console.error("Failed to create Stripe customer session:", error);
        }
      };
      fetchClientSecret();
    }
  }, [userData?.stripeCustomerId]);

  React.useEffect(() => {
    if (
      userData?.status === UserStatus.TRIAL ||
      userData?.status === UserStatus.CANCELED
    ) {
      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/pricing-table.js";
      script.async = true;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [userData?.status]);

  // Format renewal date
  const renewalDate = React.useMemo(() => {
    if (!subscriptionData?.currentPeriodEnd) return null;
    const date = new Date(subscriptionData.currentPeriodEnd * 1000);
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  }, [subscriptionData?.currentPeriodEnd]);

  // Format reset date and time
  const resetDateTime = React.useMemo(() => {
    if (!subscriptionData?.currentPeriodEnd) return null;
    const date = new Date(subscriptionData.currentPeriodEnd * 1000);
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  }, [subscriptionData?.currentPeriodEnd]);

  // Credits calculation
  const includedCredits = 200;
  const creditsUsed = totalUsage;
  const creditsRemaining = Math.max(0, includedCredits - creditsUsed);
  const overageCredits = Math.max(0, creditsUsed - includedCredits);

  const [isManageLoading, setIsManageLoading] = React.useState(false);

  const handleManageSubscription = async () => {
    setIsManageLoading(true);
    try {
      const createStripePortalSession = httpsCallable(
        functions,
        "createStripePortalSession",
        { timeout: 150_000 }
      );
      const { data } = await createStripePortalSession({
        returnUrl: window.location.href,
      });
      // @ts-ignore
      if (data.url) {
        // @ts-ignore
        window.location.href = data.url;
      } else {
        setIsManageLoading(false);
      }
    } catch (error) {
      console.error("Failed to create Stripe portal session:", error);
      setIsManageLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <section>
          {!hideHeader && (
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t("settings.accountStatus")}
            </h2>
          )}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-4 mb-6">
              <Skeleton className="w-12 h-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-32" />
              </div>
              <Skeleton className="ml-auto h-10 w-24 rounded-lg" />
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-start justify-between mb-4">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-5 w-20" />
              </div>
              <Skeleton className="w-full h-2 mb-4 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-56" />
                <Skeleton className="h-4 w-48" />
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <ReferralHubCard />

      <section className="min-w-0">
        {!hideHeader && (
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t("settings.accountStatus")}
          </h2>
        )}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center gap-4 mb-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shrink-0">
              <CreditCardIcon className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900">
                {userData?.status === UserStatus.ACTIVE &&
                subscriptionData?.planName
                  ? t("settings.youreOnPlan", {
                      planName: subscriptionData.planName,
                    })
                  : userData?.status === UserStatus.ACTIVE
                    ? t("settings.statusActive")
                    : userData?.status === UserStatus.TRIAL
                      ? t("settings.statusTrial")
                      : userData?.status === UserStatus.CANCELED
                        ? t("settings.statusCanceled")
                        : t("settings.statusIncomplete")}
              </h3>
              {userData?.status === UserStatus.ACTIVE && renewalDate ? (
                <p className="text-sm text-gray-500">
                  {t("settings.renews", { date: renewalDate })}
                </p>
              ) : userData?.status === UserStatus.ACTIVE &&
                isLoadingSubscription ? (
                <Skeleton className="h-4 w-32 mt-1" />
              ) : null}
              {userData?.status !== UserStatus.ACTIVE && (
                <p className="text-sm text-gray-500">
                  {t("settings.creditsAvailable")}:{" "}
                  <span className="font-mono font-semibold text-black">
                    {formatCreditAmount(Math.max(0, userData?.credits ?? 0))}
                  </span>
                </p>
              )}
            </div>
            {userData?.status === UserStatus.ACTIVE && (
              <button
                onClick={handleManageSubscription}
                disabled={isManageLoading}
                className="ml-auto shrink-0 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isManageLoading && (
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                )}
                <span>{t("settings.manage")}</span>
              </button>
            )}
          </div>
          {userData?.status === UserStatus.ACTIVE && (
            <p className="text-xs text-gray-400 text-right mb-6">
              {t("settings.manageHint")}
            </p>
          )}

          {/* Usage display for ACTIVE users */}
          {userData?.status === UserStatus.ACTIVE &&
            (isLoadingSubscription ? (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-start justify-between mb-4">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-5 w-20" />
                </div>
                <Skeleton className="w-full h-2 mb-4 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-56" />
                  <Skeleton className="h-4 w-48" />
                </div>
              </div>
            ) : (
              subscriptionData?.hasSubscription && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-start justify-between mb-4">
                    <h4 className="font-semibold text-gray-900">
                      {overageCredits > 0
                        ? t("settings.additionalCredits")
                        : t("settings.includedCreditsRemaining")}
                    </h4>
                    <p className="text-sm font-mono font-semibold text-gray-900">
                      {overageCredits > 0
                        ? t("settings.additionalCreditsCount", {
                            count: Math.round(overageCredits * 10) / 10,
                          })
                        : t("settings.creditsOf", {
                            remaining: creditsRemaining.toFixed(1),
                            total: includedCredits,
                          })}
                    </p>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full bg-gray-100 rounded-full h-2 mb-4 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${
                        overageCredits > 0
                          ? "bg-gradient-to-r from-orange-500 to-red-500"
                          : "bg-gradient-to-r from-blue-500 to-blue-600"
                      }`}
                      style={{
                        width: `${Math.min(100, (creditsUsed / includedCredits) * 100)}%`,
                      }}
                    />
                  </div>

                  {/* Usage details */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <svg
                        className="w-4 h-4 text-blue-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>
                        {t("settings.creditsIncludedPerPeriod", {
                          count: includedCredits,
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <svg
                        className="w-4 h-4 text-blue-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                      </svg>
                      <span>
                        {t("settings.creditsUsedThisPeriod", {
                          count: Math.round(creditsUsed * 10) / 10,
                        })}
                      </span>
                    </div>
                    {overageCredits > 0 && (
                      <div className="flex items-center gap-2 text-sm text-amber-600 pt-2 border-t border-gray-100">
                        <svg
                          className="w-4 h-4 text-amber-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>
                          {t("settings.overageCreditsWillBeBilled", {
                            count: Math.round(overageCredits * 10) / 10,
                          })}
                        </span>
                      </div>
                    )}
                    {resetDateTime && (
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>
                          {t("settings.resetsOn", { date: resetDateTime })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )
            ))}

          {/* Upcoming Invoice Preview for ACTIVE users */}
          {userData?.status === UserStatus.ACTIVE &&
            subscriptionData?.hasSubscription &&
            (isLoadingInvoice ? (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <Skeleton className="h-6 w-32 mb-4" />
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                  <div className="pt-3 border-t border-gray-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-12" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              upcomingInvoice && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-4">
                    {t("settings.upcomingCharges")}
                  </h4>
                  <div className="space-y-3">
                    {upcomingInvoice.lineItems.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between text-sm"
                      >
                        <div className="flex-1">
                          <p className="text-gray-900">{item.description}</p>
                        </div>
                        <p className="font-mono text-gray-900">
                          {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: item.currency.toUpperCase(),
                          }).format(item.amount / 100)}
                        </p>
                      </div>
                    ))}
                    <div className="pt-3 border-t border-gray-200 space-y-2">
                      {upcomingInvoice.tax !== null &&
                        upcomingInvoice.tax > 0 && (
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex-1">
                              <p className="text-gray-900">
                                {t("settings.tax")}
                              </p>
                            </div>
                            <p className="font-mono text-gray-900">
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency:
                                  upcomingInvoice.currency.toUpperCase(),
                              }).format(upcomingInvoice.tax / 100)}
                            </p>
                          </div>
                        )}
                      <div className="flex items-center justify-between text-sm">
                        <p className="font-semibold text-gray-900">
                          {t("settings.total")}
                        </p>
                        <p className="font-mono font-semibold text-gray-900">
                          {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: upcomingInvoice.currency.toUpperCase(),
                          }).format(upcomingInvoice.total / 100)}
                        </p>
                      </div>
                    </div>
                    {upcomingInvoice.nextPaymentAttempt && (
                      <p className="text-xs text-gray-500 pt-2">
                        {t("settings.nextPaymentOn")}{" "}
                        {new Intl.DateTimeFormat("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        }).format(
                          new Date(upcomingInvoice.nextPaymentAttempt * 1000)
                        )}
                      </p>
                    )}
                  </div>
                </div>
              )
            ))}

          {(userData?.status === UserStatus.TRIAL ||
            userData?.status === UserStatus.CANCELED) &&
            customerSessionClientSecret && (
              <div className="mt-8">
                {userData?.status === UserStatus.CANCELED && (
                  <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <h4 className="font-semibold text-amber-900">
                      {t("settings.canceledBannerTitle")}
                    </h4>
                    <p className="mt-1 text-sm text-amber-800">
                      {t("settings.canceledBannerDescription")}
                    </p>
                  </div>
                )}
                {/* @ts-expect-error - Stripe pricing table is a custom web component */}
                <stripe-pricing-table
                  pricing-table-id={
                    import.meta.env.VITE_STRIPE_PRICING_TABLE_ID
                  }
                  publishable-key={import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY}
                  customer-session-client-secret={customerSessionClientSecret}
                />
              </div>
            )}
        </div>
      </section>

      {/* Credits per generation — side-by-side with account status on lg+ */}
      <section className="min-w-0">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t("settings.creditsPerGeneration")}
        </h2>
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <p className="text-sm text-gray-700 mb-4">
            {t("settings.creditsPerGenerationHelp")}
          </p>
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="border-b border-gray-200 bg-gray-100 px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-900">
                {t("settings.imageGeneration")}
              </h3>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    {t("settings.resolution")}
                  </th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900">
                    {t("studio.editModeQuality")}
                  </th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900">
                    {t("studio.editModeFast")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 text-gray-900">1K</td>
                  <CreditAmountCell value={COST_IMAGE_1K} />
                  <CreditAmountCell value={COST_FAST_1K} />
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 text-gray-900">2K</td>
                  <CreditAmountCell value={COST_IMAGE_2K} />
                  <CreditAmountCell value={COST_FAST_2K} />
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 text-gray-900">4K</td>
                  <CreditAmountCell value={COST_IMAGE_4K} />
                  <CreditAmountCell value={COST_FAST_4K} />
                </tr>
              </tbody>
            </table>
            <div className="border-t border-b border-gray-200 bg-gray-100 px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-900">
                {t("settings.videoGeneration")}
              </h3>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    {t("video.panel.resolution")}
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    {t("video.panel.model")}
                  </th>
                  {VIDEO_PRICING_DURATIONS.map((duration) => (
                    <th
                      key={duration}
                      className="text-right py-3 px-4 font-semibold text-gray-900"
                    >
                      {duration}s
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white">
                {VIDEO_PRICING_MODELS.flatMap((model) =>
                  VIDEO_PRICING_RESOLUTIONS.map((resolution) => (
                    <tr
                      key={`${model.id}-${resolution}`}
                      className="border-b border-gray-100"
                    >
                      <td className="py-3 px-4 text-gray-900">{resolution}</td>
                      <td className="py-3 px-4 text-gray-900">
                        {t(model.labelKey)}
                      </td>
                      {VIDEO_PRICING_DURATIONS.map((duration) => (
                        <CreditAmountCell
                          key={duration}
                          value={getVideoGenerationCreditCost({
                            modelId: model.id,
                            resolution,
                            duration,
                          })}
                        />
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <table className="w-full text-sm border-t border-gray-200">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    {t("settings.creditsOtherOperations")}
                  </th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900">
                    {t("settings.credits")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 text-gray-900">
                    {t("studio.removeBackgroundShort")}
                  </td>
                  <CreditAmountCell value={COST_BG_REMOVE} />
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 text-gray-900">
                    {t("studio.upscaleShort")} · {t("studio.upscalerStandard")}
                  </td>
                  <CreditAmountCell value={COST_UPSCALE} />
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 text-gray-900">
                    {t("studio.upscaleShort")} · {t("studio.upscalerPremium")}
                    <span className="block text-xs text-gray-500">
                      {t("settings.upscalePremiumTiers", {
                        tiers: reachableUpscaleTiers
                          .map((tier) =>
                            t("settings.upscalePremiumTier", {
                              cost: formatCreditAmount(tier.cost),
                              maxMp: tier.maxMegapixels,
                            })
                          )
                          .join(", "),
                      })}
                    </span>
                  </td>
                  <CreditAmountCell
                    value={reachableUpscaleTiers[0].cost}
                    suffix={
                      reachableUpscaleTiers.length > 1
                        ? `–${formatCreditAmount(
                            reachableUpscaleTiers[
                              reachableUpscaleTiers.length - 1
                            ].cost
                          )}`
                        : undefined
                    }
                  />
                </tr>
                <tr>
                  <td className="py-3 px-4 text-gray-900">
                    {t("settings.allOtherGenerationTypes")}
                  </td>
                  <CreditAmountCell value={COST_EDITS} />
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex items-start gap-3 p-4 mt-4 rounded-lg bg-blue-50 border border-blue-100 text-sm text-gray-700">
            <svg
              className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <span>{t("settings.overageInfoBanner")}</span>
          </div>
        </div>
      </section>

      {subscriptionData?.hasSubscription && <CreditGrantsTable />}

      {/* Enterprise offering — full width */}
      <section className="min-w-0 lg:col-span-2">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t("settings.enterpriseHeading")}
        </h2>
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <p className="text-sm text-gray-700 mb-4">
            {t("settings.enterpriseDescription")}
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <a
              href="https://meetings.hubspot.com/p-schlieker/showcase-demo"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex justify-center items-center px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium bg-white hover:bg-gray-50 transition-colors"
            >
              {t("settings.bookPersonalMeeting")}
            </a>
            <span className="text-sm text-gray-500 sm:self-center">
              {t("settings.orEmailUs")}{" "}
              <a
                href="mailto:hello@getshowcase.ai"
                className="text-blue-600 hover:text-blue-500 font-medium"
              >
                hello@getshowcase.ai
              </a>
            </span>
          </div>
        </div>
      </section>
    </div>
  );
};
